import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';

const AGENCY_JWT_KEY = 'agency_jwt';
const API_URL = process.env.REACT_APP_API_URL || 'https://videoreel-api-habwcma9cygwhphm.westus-01.azurewebsites.net';

function getAgencyToken() {
  return localStorage.getItem(AGENCY_JWT_KEY) || '';
}

// ─── Styled components ────────────────────────────────────────────────────────

const Wrap = styled.div`color: white;`;

const Title = styled.h1`
  font-size: 1.35rem;
  font-weight: 700;
  margin: 0 0 4px;
  background: linear-gradient(90deg, #fff, rgba(255,255,255,0.7));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const SubTitle = styled.p`
  font-size: 0.82rem;
  color: rgba(255,255,255,0.4);
  margin: 0 0 24px;
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 28px;
  flex-wrap: wrap;
`;

const PeriodSelect = styled.select`
  background: rgba(255,255,255,0.05);
  border: 1px solid rgba(255,255,255,0.12);
  border-radius: 8px;
  color: white;
  font-size: 0.88rem;
  padding: 8px 12px;
  outline: none;
  cursor: pointer;
  &:focus { border-color: rgba(255,71,87,0.5); }
  option { background: #1a1a2e; }
`;

const PubSelect = styled(PeriodSelect)`
  min-width: 180px;
`;

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 16px;
  margin-bottom: 28px;
`;

const KpiCard = styled.div`
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 14px;
  padding: 20px 18px;
`;

const KpiLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  color: rgba(255,255,255,0.35);
  margin-bottom: 8px;
`;

const KpiValue = styled.div`
  font-size: 1.9rem;
  font-weight: 700;
  color: white;
  line-height: 1;
`;

const KpiSub = styled.div`
  font-size: 0.72rem;
  color: rgba(255,255,255,0.35);
  margin-top: 4px;
`;

const Section = styled.div`
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px;
  padding: 20px 22px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.div`
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(255,255,255,0.35);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255,255,255,0.06);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
`;

const Th = styled.th`
  text-align: left;
  padding: 8px 10px;
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255,255,255,0.35);
  border-bottom: 1px solid rgba(255,255,255,0.06);
  white-space: nowrap;
`;

const Td = styled.td`
  padding: 10px 10px;
  color: rgba(255,255,255,0.75);
  border-bottom: 1px solid rgba(255,255,255,0.04);
  &:first-child { color: white; }
`;

const Rate = styled.span`
  color: ${p => p.$good ? '#2ecc71' : p.$mid ? '#f9ca24' : 'rgba(255,255,255,0.45)'};
`;

const PubBreakdownWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
`;

const PubPill = styled.span`
  font-size: 0.68rem;
  padding: 2px 8px;
  background: rgba(255,71,87,0.08);
  border: 1px solid rgba(255,71,87,0.18);
  color: #ff7c89;
  border-radius: 20px;
`;

const EmptyMsg = styled.div`
  text-align: center;
  padding: 48px 0;
  color: rgba(255,255,255,0.3);
  font-size: 0.88rem;
`;

const ErrorMsg = styled.div`
  background: rgba(255,71,87,0.08);
  border: 1px solid rgba(255,71,87,0.2);
  border-radius: 10px;
  padding: 14px 16px;
  color: #ff7c89;
  font-size: 0.85rem;
  margin-bottom: 20px;
`;

const ExpandBtn = styled.button`
  background: none;
  border: none;
  color: rgba(255,255,255,0.35);
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0;
  &:hover { color: rgba(255,255,255,0.7); }
`;

// ─── Period helpers ────────────────────────────────────────────────────────────

function buildPeriodOptions() {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
    opts.push({ val, label });
  }
  return opts;
}

const PERIOD_OPTIONS = buildPeriodOptions();

// ─── Row with expandable publication breakdown ─────────────────────────────────

function AdRow({ ad }) {
  const [expanded, setExpanded] = useState(false);
  const rate = ad.completion_rate;
  const rateColor = rate >= 0.7 ? 'good' : rate >= 0.4 ? 'mid' : '';

  return (
    <>
      <tr>
        <Td>
          {ad.title || ad.video_id}
          {ad.publication_breakdown?.length > 0 && (
            <ExpandBtn onClick={() => setExpanded(e => !e)} title="Show publication breakdown">
              {' '}{expanded ? '▲' : '▼'}
            </ExpandBtn>
          )}
        </Td>
        <Td>{ad.unique_viewers}</Td>
        <Td>{ad.impressions.toLocaleString()}</Td>
        <Td>{ad.completions.toLocaleString()}</Td>
        <Td>{ad.clicks.toLocaleString()}</Td>
        <Td>
          <Rate $good={rateColor === 'good'} $mid={rateColor === 'mid'}>
            {(rate * 100).toFixed(1)}%
          </Rate>
        </Td>
        <Td>{ad.avg_watch_percentage}%</Td>
      </tr>
      {expanded && ad.publication_breakdown?.length > 0 && (
        <tr>
          <Td colSpan={7} style={{ paddingTop: 0, paddingBottom: 12 }}>
            <PubBreakdownWrap>
              {ad.publication_breakdown.map(pb => (
                <PubPill key={pb.publication_id}>
                  {pb.publication_id}: {pb.impressions.toLocaleString()} imp
                </PubPill>
              ))}
            </PubBreakdownWrap>
          </Td>
        </tr>
      )}
    </>
  );
}

// ─── Main component ────────────────────────────────────────────────────────────

const AgencyAnalyticsDashboard = () => {
  const [period, setPeriod] = useState(PERIOD_OPTIONS[0].val);
  const [pubFilter, setPubFilter] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = getAgencyToken();
      const res = await fetch(`${API_URL}/api/agency/analytics?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`Server error (${res.status})`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPubFilter('all'); }, [period]);

  // Derive unique publication IDs from loaded data
  const allPubs = React.useMemo(() => {
    if (!data) return [];
    const ids = new Set();
    data.ads.forEach(ad => (ad.publication_breakdown || []).forEach(pb => ids.add(pb.publication_id)));
    return [...ids].sort();
  }, [data]);

  // Filter ads by selected publication and recompute totals
  const filteredAds = React.useMemo(() => {
    if (!data) return [];
    if (pubFilter === 'all') return data.ads;
    return data.ads
      .map(ad => {
        const pb = (ad.publication_breakdown || []).filter(p => p.publication_id === pubFilter);
        if (!pb.length) return null;
        const pubImpressions = pb[0].impressions;
        const ratio = ad.impressions > 0 ? pubImpressions / ad.impressions : 0;
        return {
          ...ad,
          impressions: pubImpressions,
          completions: Math.round(ad.completions * ratio),
          clicks: Math.round(ad.clicks * ratio),
          publication_breakdown: pb,
        };
      })
      .filter(Boolean);
  }, [data, pubFilter]);

  const filteredTotals = React.useMemo(() => {
    if (!filteredAds.length) return { impressions: 0, completions: 0, clicks: 0, unique_viewers: 0 };
    return {
      impressions: filteredAds.reduce((s, a) => s + a.impressions, 0),
      completions: filteredAds.reduce((s, a) => s + a.completions, 0),
      clicks: filteredAds.reduce((s, a) => s + a.clicks, 0),
      unique_viewers: pubFilter === 'all' ? (data?.totals?.unique_viewers || 0) : '—',
    };
  }, [filteredAds, pubFilter, data]);

  return (
    <Wrap>
      <Title>Ad Analytics</Title>
      <SubTitle>Impression, completion and click data for your ads.</SubTitle>

      <Controls>
        <PeriodSelect value={period} onChange={e => setPeriod(e.target.value)}>
          {PERIOD_OPTIONS.map(o => (
            <option key={o.val} value={o.val}>{o.label}</option>
          ))}
        </PeriodSelect>
        {allPubs.length > 0 && (
          <PubSelect value={pubFilter} onChange={e => setPubFilter(e.target.value)}>
            <option value="all">All publications</option>
            {allPubs.map(p => <option key={p} value={p}>{p}</option>)}
          </PubSelect>
        )}
        {loading && <span style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.35)' }}>Loading…</span>}
      </Controls>

      {error && <ErrorMsg>{error}</ErrorMsg>}

      {data && (
        <>
          <KpiRow>
            <KpiCard>
              <KpiLabel>Impressions</KpiLabel>
              <KpiValue>{filteredTotals.impressions.toLocaleString()}</KpiValue>
              <KpiSub>ad views</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Unique Viewers</KpiLabel>
              <KpiValue>{typeof filteredTotals.unique_viewers === 'number' ? filteredTotals.unique_viewers.toLocaleString() : filteredTotals.unique_viewers}</KpiValue>
              <KpiSub>distinct subscribers</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Completions</KpiLabel>
              <KpiValue>{filteredTotals.completions.toLocaleString()}</KpiValue>
              <KpiSub>watched to end</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Clicks</KpiLabel>
              <KpiValue>{filteredTotals.clicks.toLocaleString()}</KpiValue>
              <KpiSub>CTA taps</KpiSub>
            </KpiCard>
            <KpiCard>
              <KpiLabel>Completion Rate</KpiLabel>
              <KpiValue>
                {filteredTotals.impressions > 0
                  ? `${((filteredTotals.completions / filteredTotals.impressions) * 100).toFixed(1)}%`
                  : '—'}
              </KpiValue>
              <KpiSub>completions / impressions</KpiSub>
            </KpiCard>
          </KpiRow>

          <Section>
            <SectionTitle>Per-ad breakdown</SectionTitle>
            {filteredAds.length === 0 ? (
              <EmptyMsg>{pubFilter === 'all' ? 'No ad data recorded for this period yet.' : `No impressions for "${pubFilter}" this period.`}</EmptyMsg>
            ) : (
              <Table>
                <thead>
                  <tr>
                    <Th>Ad title</Th>
                    <Th>Unique viewers</Th>
                    <Th>Impressions</Th>
                    <Th>Completions</Th>
                    <Th>Clicks</Th>
                    <Th>Completion rate</Th>
                    <Th>Avg watch %</Th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAds.map(ad => <AdRow key={ad.video_id} ad={ad} />)}
                </tbody>
              </Table>
            )}
          </Section>
        </>
      )}

      {!loading && !error && !data && (
        <EmptyMsg>Select a period to load analytics.</EmptyMsg>
      )}
    </Wrap>
  );
};

export default AgencyAnalyticsDashboard;
